// frontend/src/pages/Agreements/AddendumNew.jsx
import { useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { getEntities } from "../../api/entity.api.js";
import {
  getAgreement,
  submitAddendum,
  uploadAddendumFile,
} from "../../api/agreement.api.js";
import {
  FormField,
  ErrorMessage,
  Spinner,
  formatDate,
} from "../../components/ui";
import { ChevronLeft, Upload } from "lucide-react";

export default function AddendumNew() {
  const { id: agreementId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { isMaster } = useAuth();

  const [form, setForm] = useState({
    title: "",
    description: "",
    // All fields below are optional — only fill if changing
    entityCacheId: "",
    startDate: "",
    endDate: "",
    scope: "",
    objectOfAgreement: "",
    stakeholders: "",
    paymentDeadline: "",
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const { data: agreement, isLoading } = useQuery({
    queryKey: ["agreement", agreementId],
    queryFn: () => getAgreement(agreementId),
  });

  const { data: entities } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
    enabled: isMaster,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        description: form.description,
        entityCacheId: form.entityCacheId || undefined,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        scope: form.scope || undefined,
        objectOfAgreement: form.objectOfAgreement || undefined,
        stakeholders: form.stakeholders || undefined,
        paymentDeadline: form.paymentDeadline || undefined,
      };
      const addendum = await submitAddendum(agreementId, payload);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadAddendumFile(agreementId, addendum.id, fd);
      }
      return addendum;
    },
    onSuccess: () => navigate(`/agreements/${agreementId}`),
    onError: (err) => setError(err),
  });

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    submit.mutate();
  }

  if (isLoading) return <Spinner />;

  return (
    <div>
      <Link
        to={`/agreements/${agreementId}`}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Back to agreement
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Addendum</h1>
        {agreement && (
          <p className="text-sm text-gray-500 mt-1">
            For:{" "}
            <span className="font-medium text-gray-700">{agreement.title}</span>
          </p>
        )}
      </div>

      {/* Current agreement values for reference */}
      {agreement && (
        <div className="card p-4 mb-6 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Current Agreement Values (for reference)
          </p>
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-xs">
            {[
              ["Entity", agreement.entityCache?.name],
              ["Start Date", formatDate(agreement.startDate)],
              ["End Date", formatDate(agreement.endDate)],
              ["Payment Deadline", formatDate(agreement.paymentDeadline)],
              ["Scope", agreement.scope],
              ["Object", agreement.objectOfAgreement],
              ["Stakeholders", agreement.stakeholders],
            ]
              .filter(([, v]) => v && v !== "—")
              .map(([k, v]) => (
                <div key={k}>
                  <dt className="text-gray-400 uppercase tracking-wide">{k}</dt>
                  <dd className="text-gray-700 font-medium truncate">{v}</dd>
                </div>
              ))}
          </dl>
        </div>
      )}

      <div className="card p-6 max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Required fields */}
          <FormField label="Addendum Title" required>
            <input
              className="input"
              value={form.title}
              onChange={set("title")}
              placeholder="e.g. Addendum I — Perpanjangan Waktu"
              required
            />
          </FormField>

          <FormField label="Description of Changes" required>
            <textarea
              className="input"
              rows={4}
              value={form.description}
              onChange={set("description")}
              placeholder="Describe what this addendum changes and why..."
              required
            />
          </FormField>

          {/* Optional fields — only fill what's changing */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Fields being changed (leave blank if not changing)
            </p>

            <div className="space-y-4">
              {/* Entity — master admin only */}
              {isMaster && (
                <FormField label="New Rhaya Entity (PT)">
                  <select
                    className="input"
                    value={form.entityCacheId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, entityCacheId: e.target.value }))
                    }
                  >
                    <option value="">
                      Keep current (
                      {agreement?.entityCache?.name ?? "unassigned"})
                    </option>
                    {entities?.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.name}
                      </option>
                    ))}
                  </select>
                </FormField>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormField label="New Start Date">
                  <input
                    className="input"
                    type="date"
                    value={form.startDate}
                    onChange={set("startDate")}
                  />
                </FormField>
                <FormField label="New End Date">
                  <input
                    className="input"
                    type="date"
                    value={form.endDate}
                    onChange={set("endDate")}
                    min={form.startDate}
                  />
                </FormField>
              </div>

              <FormField label="New Payment Deadline">
                <input
                  className="input"
                  type="date"
                  value={form.paymentDeadline}
                  onChange={set("paymentDeadline")}
                />
              </FormField>

              <FormField label="New Scope">
                <textarea
                  className="input"
                  rows={3}
                  value={form.scope}
                  onChange={set("scope")}
                />
              </FormField>

              <FormField label="New Object of Agreement">
                <textarea
                  className="input"
                  rows={2}
                  value={form.objectOfAgreement}
                  onChange={set("objectOfAgreement")}
                />
              </FormField>

              <FormField label="New Stakeholders">
                <input
                  className="input"
                  value={form.stakeholders}
                  onChange={set("stakeholders")}
                />
              </FormField>
            </div>
          </div>

          {/* PDF upload */}
          <FormField label="Addendum Document (PDF)">
            <label
              className={`flex items-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                file
                  ? "border-brand-500 bg-brand-50"
                  : "border-gray-300 hover:border-brand-400"
              }`}
            >
              <Upload
                size={18}
                className={file ? "text-brand-500" : "text-gray-400"}
              />
              <div>
                {file ? (
                  <p className="text-sm font-medium text-brand-700">
                    {file.name}
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      Click to upload addendum PDF
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      PDF only · max 10MB
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="mt-1 text-xs text-red-500 hover:underline"
              >
                Remove file
              </button>
            )}
          </FormField>

          {error && <ErrorMessage error={error} />}

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button
              type="submit"
              disabled={submit.isPending}
              className="btn-primary"
            >
              {submit.isPending ? "Submitting..." : "Submit Addendum"}
            </button>
            <Link to={`/agreements/${agreementId}`} className="btn-secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
